import * as React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UseFormReturn, useFormContext } from 'react-hook-form';

import * as SelectPrimitive from '@radix-ui/react-select';
import get from 'lodash/get';
import { Check, ChevronDown, ChevronUp, Search, X } from 'lucide-react';

import { Typography } from 'components/typography';
import { useExtendedFormContext } from 'contexts/extendedFormContext';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { getDrumkitContainer } from 'utils/getDrumkitContainer';
import { cn } from 'utils/shadcn';

export type BaseOptionType = {
  label: React.ReactNode;
  value: string | number;
  key?: string;
  disabled?: boolean;
  title?: string;
  group?: string;
  options?: BaseOptionType[];
  [key: string]: any;
};

function preferredSearchHighlight(
  options: BaseOptionType[],
  currentValue: Undef<string>,
  preferSelected: boolean
): Maybe<string> {
  const currentOption =
    preferSelected && currentValue
      ? options.find(
          (option) => !option.disabled && String(option.value) === currentValue
        )
      : undefined;
  const highlightedOption =
    currentOption ?? options.find((option) => !option.disabled);

  return highlightedOption ? String(highlightedOption.value) : null;
}

function optionLabelToSearchString(label: React.ReactNode): string {
  if (typeof label === 'string') {
    return label;
  }
  if (typeof label === 'number') {
    return String(label);
  }
  return String(label || '');
}

function nextEnabledOptionIndex(
  currentIndex: number,
  optionCount: number,
  key: 'ArrowDown' | 'ArrowUp'
): number {
  if (currentIndex === -1) {
    if (key === 'ArrowDown') {
      return 0;
    }
    return optionCount - 1;
  }
  const delta = key === 'ArrowDown' ? 1 : -1;
  return (currentIndex + delta + optionCount) % optionCount;
}

export type SelectValue = MaybeUndef<
  | string
  | number
  | string[]
  | number[]
  | { label: React.ReactNode; value: string | number }
>;

export function defaultFilterOption(
  input: string,
  option: Undef<BaseOptionType>
): boolean {
  if (!option?.label) {
    return false;
  }
  return String(option.label).toLowerCase().includes(input.toLowerCase());
}

export function defaultFilterSort(
  optionA: BaseOptionType,
  optionB: BaseOptionType
): number {
  const labelA = String(optionA?.label);
  const labelB = String(optionB?.label);
  return labelA.toLowerCase().localeCompare(labelB.toLowerCase());
}

interface SelectContextType {
  // Options and filtering
  options?: BaseOptionType[];
  flattenedOptions?: BaseOptionType[];
  filteredOptions?: BaseOptionType[];
  groupedOptions?: Array<[Undef<string>, BaseOptionType[]]>;
  showSearch?: boolean;
  filterOption?:
    boolean | ((input: string, option: Undef<BaseOptionType>) => boolean);
  filterSort?: (optionA: BaseOptionType, optionB: BaseOptionType) => number;
  optionRender?: (option: BaseOptionType) => React.ReactNode;
  notFoundContent?: React.ReactNode;
  // Search state
  searchValue: string;
  setSearchValue: (value: string) => void;
  searchInputRef: React.MutableRefObject<Maybe<HTMLInputElement>>;
  onSearch?: (searchText: string) => void;
  // Value handling
  labelInValue?: boolean;
  currentValue?: string;
  onChange?: (
    value: SelectValue,
    option?: BaseOptionType | BaseOptionType[]
  ) => void;
  onSelect?: (value: SelectValue, option: BaseOptionType) => void;
  handleValueChange?: (value: string) => void;
  // Highlighting
  highlightedOptionValue: Maybe<string>;
  setHighlightedOptionValue: (value: Maybe<string>) => void;
  setOpen?: (isOpen: boolean) => void;
}

const SelectContext = createContext<Maybe<SelectContextType>>(null);

const useSelectContext = () => {
  const context = useContext(SelectContext);
  return context;
};

export interface SelectRootProps extends Omit<
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root>,
  'value' | 'defaultValue'
> {
  options?: BaseOptionType[];
  showSearch?: boolean;
  // Vehicle make/model: keep the current value highlighted so Enter does not
  // jump to the first filtered row. Other searchable selects stay first-match.
  preferSelectedHighlight?: boolean;
  filterOption?:
    boolean | ((input: string, option: Undef<BaseOptionType>) => boolean);
  filterSort?: (optionA: BaseOptionType, optionB: BaseOptionType) => number;
  optionFilterProp?: string;
  optionRender?: (option: BaseOptionType) => React.ReactNode;
  notFoundContent?: React.ReactNode;
  labelInValue?: boolean;
  onSearch?: (searchText: string) => void;
  onChange?: (
    value: SelectValue,
    option?: BaseOptionType | BaseOptionType[]
  ) => void;
  onSelect?: (value: SelectValue, option: BaseOptionType) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  // Props that should be passed to SelectTrigger when using options API
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  disabled?: boolean;
  variant?: 'borderless' | 'outlined' | 'filled';
  allowClear?: boolean;
  name?: string;
  value?: SelectValue | string;
  defaultValue?: SelectValue | string;
  'data-testid'?: string;
}

const SelectRoot = ({
  options,
  showSearch = false,
  preferSelectedHighlight = false,
  filterOption = defaultFilterOption,
  filterSort = defaultFilterSort,
  optionRender,
  notFoundContent,
  labelInValue = false,
  onSearch,
  onChange,
  onSelect,
  onFocus,
  onBlur,
  value,
  defaultValue,
  onValueChange,
  open,
  onOpenChange,
  // Trigger props
  className,
  style,
  placeholder = 'Choose',
  disabled = false,
  variant = 'outlined',
  allowClear = false,
  name,
  'data-testid': dataTestId,
  // Children for manual composition
  children,
  ...props
}: SelectRootProps) => {
  const [searchValue, setSearchValue] = useState('');
  const [highlightedOptionValue, setHighlightedOptionValue] =
    useState<Maybe<string>>(null);
  const [internalOpen, setInternalOpen] = useState(open ?? false);
  const searchInputRef = useRef<Maybe<HTMLInputElement>>(null);
  const isControlledOpen = open !== undefined;

  const actualOpen = isControlledOpen ? open : internalOpen;

  const flattenedOptions = useMemo(() => {
    if (!options) return [];
    const flattened: BaseOptionType[] = [];

    const processOption = (option: BaseOptionType, parentTitle?: string) => {
      if (option.options && Array.isArray(option.options)) {
        const groupTitle = option.title || option.group || parentTitle;
        option.options.forEach((childOption) => {
          const processedChild: BaseOptionType = {
            ...childOption,
            title: childOption.title || groupTitle,
          };
          flattened.push(processedChild);
        });
      } else {
        if (option.value === '__group_header__') {
          return;
        }
        const processedOption: BaseOptionType = {
          ...option,
          title: option.title || option.group || parentTitle,
        };
        flattened.push(processedOption);
      }
    };

    options.forEach((option) => processOption(option));
    return flattened;
  }, [options]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!showSearch || !searchValue) {
      return flattenedOptions;
    }

    let filtered = flattenedOptions;

    if (typeof filterOption === 'function') {
      filtered = flattenedOptions.filter((option) =>
        filterOption(searchValue, option)
      );
    } else if (filterOption === true) {
      filtered = flattenedOptions.filter((option) => {
        const labelStr = optionLabelToSearchString(option.label);
        const search = searchValue.toLowerCase();
        return labelStr.toLowerCase().includes(search);
      });
    }

    if (filterSort) {
      filtered = [...filtered].sort(filterSort);
    }

    return filtered;
  }, [flattenedOptions, searchValue, filterOption, filterSort, showSearch]);

  // Group options by group label
  const groupedOptions = useMemo(() => {
    const groups: Map<Undef<string>, BaseOptionType[]> = new Map();

    filteredOptions.forEach((option) => {
      const groupKey = option.title || option.group || undefined;
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(option);
    });

    return Array.from(groups.entries());
  }, [filteredOptions]);

  // Convert value to string for internal use
  const stringValue = useMemo(() => {
    if (value === null || value === undefined) return undefined;
    // If options are provided, handle SelectValue type
    if (options !== undefined) {
      if (
        labelInValue &&
        typeof value === 'object' &&
        value !== null &&
        'value' in value
      ) {
        const objValue = value as {
          label: React.ReactNode;
          value: string | number;
        };
        return String(objValue.value);
      }
      return String(value);
    }
    // Otherwise, treat as string (shadcn primitive API)
    return typeof value === 'string' ? value : String(value);
  }, [value, labelInValue, options]);

  // Convert defaultValue to string
  const stringDefaultValue = useMemo(() => {
    if (defaultValue === null || defaultValue === undefined) return undefined;
    // If options are provided, handle SelectValue type
    if (options !== undefined) {
      if (
        labelInValue &&
        typeof defaultValue === 'object' &&
        defaultValue !== null &&
        'value' in defaultValue
      ) {
        const objDefaultValue = defaultValue as {
          label: React.ReactNode;
          value: string | number;
        };
        return String(objDefaultValue.value);
      }
      return String(defaultValue);
    }
    // Otherwise, treat as string (shadcn primitive API)
    return typeof defaultValue === 'string'
      ? defaultValue
      : String(defaultValue);
  }, [defaultValue, labelInValue, options]);

  // Handle value changes
  const handleValueChange = useCallback(
    (newValue: string) => {
      if (options !== undefined) {
        const selectedOption = flattenedOptions.find(
          (opt) => String(opt.value) === newValue
        );

        if (onChange) {
          if (labelInValue && selectedOption) {
            const labelValue = {
              label: selectedOption.label,
              value: selectedOption.value,
            };
            onChange(labelValue, selectedOption);
            if (selectedOption) {
              onSelect?.(labelValue, selectedOption);
            }
          } else {
            onChange(newValue, selectedOption);
            if (selectedOption) {
              onSelect?.(newValue, selectedOption);
            }
          }
        }
      } else {
        // Otherwise, use the original shadcn onValueChange
        onValueChange?.(newValue);
      }
    },
    [options, flattenedOptions, labelInValue, onChange, onSelect, onValueChange]
  );

  // Runs when the menu opens, not whenever the options array changes.
  useEffect(() => {
    if (!actualOpen || !showSearch) {
      return;
    }

    // Skip current event loop to avoid racing conditions w/ rerender
    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, [actualOpen, showSearch]);

  // Handle open changes
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isControlledOpen) {
        setInternalOpen(isOpen);
      }
      onOpenChange?.(isOpen);

      if (isOpen) {
        const initialHighlight = preferredSearchHighlight(
          filteredOptions,
          stringValue,
          preferSelectedHighlight
        );
        if (initialHighlight) {
          setHighlightedOptionValue(initialHighlight);
        }
        onFocus?.();
        if (showSearch && searchInputRef.current) {
          setTimeout(() => searchInputRef.current?.focus(), 0);
        }
      } else {
        onBlur?.();
        setSearchValue('');
        setHighlightedOptionValue(null);
      }
    },
    [
      isControlledOpen,
      onOpenChange,
      filteredOptions,
      showSearch,
      onFocus,
      onBlur,
      stringValue,
      preferSelectedHighlight,
    ]
  );

  // Resync when search or options change, but keep keyboard highlight when the
  // highlighted row is still in the filtered list (parent re-renders rebuild
  // the options array without changing contents).
  useEffect(() => {
    if (!actualOpen || filteredOptions.length === 0) {
      return;
    }

    const highlightStillVisible =
      highlightedOptionValue != null &&
      filteredOptions.some(
        (option) =>
          !option.disabled && String(option.value) === highlightedOptionValue
      );

    if (highlightStillVisible) {
      return;
    }

    const nextHighlight = preferredSearchHighlight(
      filteredOptions,
      stringValue,
      preferSelectedHighlight
    );
    if (nextHighlight) {
      setHighlightedOptionValue(nextHighlight);
    }
  }, [
    actualOpen,
    filteredOptions,
    highlightedOptionValue,
    searchValue,
    stringValue,
    preferSelectedHighlight,
  ]);

  const contextValue: SelectContextType = {
    options,
    flattenedOptions,
    filteredOptions,
    groupedOptions,
    showSearch,
    filterOption,
    filterSort,
    optionRender,
    notFoundContent,
    searchValue,
    setSearchValue,
    searchInputRef,
    onSearch,
    labelInValue,
    currentValue: stringValue,
    onChange,
    onSelect,
    handleValueChange,
    highlightedOptionValue,
    setHighlightedOptionValue,
    setOpen: handleOpenChange,
  };

  // Find the selected option for display
  const selectedOption = useMemo(() => {
    if (!flattenedOptions || !stringValue) return null;
    return flattenedOptions.find((opt) => String(opt.value) === stringValue);
  }, [flattenedOptions, stringValue]);

  // Determine display value
  const displayValue = useMemo(() => {
    if (
      labelInValue &&
      value &&
      typeof value === 'object' &&
      value !== null &&
      'label' in value
    ) {
      return String(
        (value as { label: React.ReactNode; value: string | number }).label
      );
    }
    return selectedOption ? String(selectedOption.label) : undefined;
  }, [labelInValue, value, selectedOption]);

  const handleClear = useCallback(() => {
    onChange?.(null);
  }, [onChange]);

  return (
    <SelectContext.Provider value={contextValue}>
      <SelectPrimitive.Root
        value={stringValue}
        defaultValue={stringDefaultValue}
        onValueChange={handleValueChange}
        open={actualOpen}
        onOpenChange={handleOpenChange}
        disabled={disabled}
        {...props}
      >
        {/*
          Should render child components passed to <Select /> if there are any (which enables manual customization),
          otherwise render default trigger + content with options, even if option list is empty.
        */}
        {children || (
          <>
            <SelectTrigger
              variant={variant}
              allowClear={allowClear}
              onClear={handleClear}
              name={name}
              className={cn('h-8 mt-1', className)}
              style={style}
              disabled={disabled}
              data-testid={dataTestId}
            >
              <SelectValue placeholder={placeholder}>
                {displayValue || null}
              </SelectValue>
            </SelectTrigger>
            <SelectContent
              className='z-9999999 min-w-[200px]'
              position='popper'
            />
          </>
        )}
      </SelectPrimitive.Root>
    </SelectContext.Provider>
  );
};

SelectRoot.displayName = 'Select';

const Select = SelectRoot;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

// Enhanced SelectTrigger with allowClear and variant support
const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    className?: string;
    variant?: 'borderless' | 'outlined' | 'filled';
    allowClear?: boolean;
    onClear?: () => void;
    'data-testid'?: string;
  }
>(({ className, children, variant, allowClear, onClear, ...props }, ref) => {
  const selectContext = useSelectContext();
  // Returns null outside a FormProvider, even though its type says otherwise.
  const formContext = useFormContext() as Maybe<UseFormReturn<any>>;
  const extendedFormContext = useExtendedFormContext();

  const highlightDirtyField =
    !!formContext &&
    !!props.name &&
    (() => {
      const { formState } = formContext;
      const { dirtyFields } = formState;
      const highlightDirtyFields =
        extendedFormContext.highlightDirtyFields ?? false;
      const fieldState = formContext.getFieldState(props.name!, formState);
      const isDirty = fieldState.isDirty || get(dirtyFields, props.name);

      return Boolean(isDirty && highlightDirtyFields);
    })();

  const currentValue = props.value || selectContext?.currentValue;
  const showClearButton = allowClear && currentValue && !props.disabled;

  const isPlaceholder = !currentValue || currentValue === '';

  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        'flex h-8 w-full items-center justify-between cursor-pointer rounded-[4px] border border-neutral-input-border bg-neutral-input-bg px-3 py-2 text-[13px] text-left ring-offset-neutral-50',
        'focus:outline-none focus:border-brand-main focus:ring-brand-200',
        'disabled:cursor-not-allowed disabled:bg-neutral-input-disabled-bg disabled:border-neutral-input-disabled-border disabled:text-neutral-input-disabled-text',
        '[&>span]:line-clamp-1',
        isPlaceholder
          ? 'text-neutral-input-placeholder'
          : 'text-neutral-input-text',
        variant === 'borderless' && 'border-none shadow-none',
        className,
        highlightDirtyField && 'bg-warning-200'
      )}
      {...props}
    >
      {children}
      {showClearButton && (
        <button
          type='button'
          aria-label='Clear selection'
          title='Clear selection'
          onClick={(e) => {
            e.stopPropagation();
            onClear?.();
          }}
          className='ml-2 h-full flex items-center text-neutral-400 hover:text-neutral-600'
        >
          <X className='h-4 w-4' />
        </button>
      )}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className='flex-shrink-0 h-4 w-4 opacity-50' />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
});

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton> & {
    className?: string;
  }
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    {...props}
  >
    <ChevronUp className='h-4 w-4' />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton> & {
    className?: string;
  }
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    {...props}
  >
    <ChevronDown className='h-4 w-4' />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

// Enhanced SelectContent with search support
const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    className?: string;
    position?: 'item-aligned' | 'popper';
    renderSearch?: boolean;
  }
>(
  (
    { className, children, position = 'popper', renderSearch, ...props },
    ref
  ) => {
    const selectContext = useSelectContext();
    const shouldRenderSearch =
      renderSearch ?? selectContext?.showSearch ?? false;

    useEffect(() => {
      if (!shouldRenderSearch) {
        return;
      }

      // Wait a tick: clearing the filter re-mounts the selected item, and Radix
      // focuses it after this effect runs, taking focus from the search input.
      const timer = window.setTimeout(() => {
        selectContext?.searchInputRef?.current?.focus();
      }, 0);

      return () => window.clearTimeout(timer);
    }, [
      shouldRenderSearch,
      selectContext?.searchValue,
      selectContext?.searchInputRef,
    ]);

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();

        const highlightedValue = selectContext?.highlightedOptionValue;
        const highlightedOption = selectContext?.filteredOptions?.find(
          (option) => String(option.value) === highlightedValue
        );
        const firstSelectable = selectContext?.filteredOptions?.find(
          (option) => !option.disabled
        );
        const selectedOption = highlightedOption ?? firstSelectable;

        if (selectedOption) {
          // The search input intercepts Enter, so we explicitly trigger the shared
          // selection pipeline here to keep labelInValue, onSelect, and onValueChange
          // behavior consistent with normal option clicks.
          selectContext?.handleValueChange?.(String(selectedOption.value));
        }

        if (selectContext?.setOpen) {
          selectContext.setOpen(false);
        }

        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();

        const enabledOptions =
          selectContext?.filteredOptions?.filter(
            (option) => !option.disabled
          ) ?? [];
        if (enabledOptions.length === 0) {
          return;
        }

        const currentValue = selectContext?.highlightedOptionValue;
        const currentIndex = enabledOptions.findIndex(
          (option) => String(option.value) === currentValue
        );
        const nextIndex = nextEnabledOptionIndex(
          currentIndex,
          enabledOptions.length,
          e.key === 'ArrowDown' ? 'ArrowDown' : 'ArrowUp'
        );

        const nextOption = enabledOptions[nextIndex];
        if (nextOption && selectContext?.setHighlightedOptionValue) {
          selectContext.setHighlightedOptionValue(String(nextOption.value));
        }

        return;
      }

      if (e.key === 'Escape') {
        if (selectContext?.setOpen) {
          selectContext.setOpen(false);
        }
        return;
      }

      e.stopPropagation();
    };

    const handleContentKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        e.stopPropagation();
      }
    };

    const handleSearchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const searchText = e.target.value;
        selectContext?.setSearchValue(searchText);
        selectContext?.onSearch?.(searchText);
      },
      [selectContext]
    );

    // Render options if they exist in context
    const renderOptions = () => {
      // If options prop was not provided, use children (SelectItem components)
      if (!selectContext?.options) return children;
      if (!selectContext?.groupedOptions) return children;

      const {
        groupedOptions,
        notFoundContent,
        optionRender,
        highlightedOptionValue,
        setHighlightedOptionValue,
      } = selectContext;

      if (groupedOptions.length === 0) {
        return (
          <div className='px-2 py-4 text-center'>
            {notFoundContent || (
              <Typography variant='body-sm' className='text-neutral-500'>
                No results found
              </Typography>
            )}
          </div>
        );
      }

      return (
        <>
          {groupedOptions.map(([groupTitle, groupOptions], groupIndex) => (
            <SelectGroup key={groupTitle || `group-${groupIndex}`}>
              {groupTitle && (
                <SelectLabel
                  className={cn(
                    'pl-3 pt-2.5 pb-1 text-xs font-bold uppercase tracking-wide text-neutral-700',
                    groupIndex > 0 && 'mt-1'
                  )}
                >
                  {groupTitle}
                </SelectLabel>
              )}
              {groupOptions
                .filter((option) => {
                  // CRITICAL: Filter out options with empty string values - SelectItem cannot have empty string value
                  const optionValue = String(option.value);
                  if (optionValue === '' || optionValue.trim() === '') {
                    return false;
                  }
                  return true;
                })
                .map((option) => {
                  const optionKey = option.key || String(option.value);
                  const optionValue = String(option.value);
                  const optionContent = optionRender
                    ? optionRender(option)
                    : option.label;
                  const isHighlighted =
                    highlightedOptionValue === optionValue && !option.disabled;

                  return (
                    <SelectItem
                      key={optionKey}
                      value={optionValue}
                      disabled={option.disabled}
                      className={cn(
                        // Indent items under a group header so grouped sub-types read as
                        // children of their group (non-grouped selects are unaffected).
                        groupTitle && 'pl-6',
                        isHighlighted && 'bg-neutral-100'
                      )}
                      onMouseEnter={() => {
                        if (!option.disabled && setHighlightedOptionValue) {
                          setHighlightedOptionValue(optionValue);
                        }
                      }}
                    >
                      {optionContent}
                    </SelectItem>
                  );
                })}
              {groupIndex < groupedOptions.length - 1 && <SelectSeparator />}
            </SelectGroup>
          ))}
          {children}
        </>
      );
    };

    return (
      <SelectPrimitive.Portal container={getDrumkitContainer()}>
        <SelectPrimitive.Content
          ref={ref}
          sideOffset={4}
          className={cn(
            'relative z-50 max-h-96 min-w-32 max-w-[calc(100vw-32px)] overflow-hidden rounded border border-neutral-300 bg-neutral-input-bg text-neutral-input-text shadow-md text-[13px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            position === 'popper' &&
              'data-[side=bottom]:translate-y-0 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
            className
          )}
          position={position}
          onKeyDown={handleContentKeyDown}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.Viewport
            className={cn(
              'p-0',
              position === 'popper' &&
                'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)'
            )}
          >
            {shouldRenderSearch && (
              <div className='px-2 py-1.5 border-b border-neutral-200'>
                <div className='relative'>
                  <Search className='absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400' />
                  <input
                    ref={selectContext?.searchInputRef}
                    type='text'
                    value={selectContext?.searchValue ?? ''}
                    onChange={handleSearchChange}
                    placeholder='Search...'
                    className='w-full pl-8 pr-2 py-1 text-[13px] text-neutral-input-text placeholder:text-neutral-input-placeholder bg-neutral-input-bg border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-main'
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={handleSearchKeyDown}
                  />
                </div>
              </div>
            )}
            <div className='max-h-[300px] overflow-y-auto'>
              {renderOptions()}
            </div>
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton />
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    );
  }
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label> & {
    className?: string;
  }
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('pl-8 pr-2 py-1.5 text-[13px] font-semibold', className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    className?: string;
  }
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex flex-row w-full cursor-pointer select-none items-center rounded-sm py-1.5 px-3 pr-7 text-[13px] text-neutral-700 outline-none focus:bg-neutral-100 focus:text-neutral-900 data-disabled:pointer-events-none data-disabled:opacity-50',
      className
    )}
    {...props}
  >
    <span className='absolute right-2 flex h-3.5 w-3.5 items-center justify-center'>
      <SelectPrimitive.ItemIndicator>
        <Check className='h-4 w-4' />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator> & {
    className?: string;
  }
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-neutral-100', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
